// "The sidebar counts are out of date" — a same-tab signal.
//
// The nav badges (pending applications, pending samples, unread threads) are
// refreshed by realtime and by a tab return, and NEITHER covers the case that
// matters most: the user changing the number themselves. Reading a thread
// writes to cr_conversation_reads, and the badge's realtime subscription is on
// cr_conversations, so it hears nothing. Approving an application and reviewing
// a sample go through the worker, so they produce no client-side event either.
//
// The result was a badge that kept claiming "1" after the user had dealt with
// the thing, until some unrelated refresh happened to clear it seconds later.
//
// Same shape as onWalletChanged in lib/wallet.ts: an app-level event that
// listeners answer by refetching. Emitting twice for one action is a wasted
// read, never a wrong number.

const EVENT = 'bmc:nav-badges-stale'

/** Bursts are collapsed: the mark-read effect in Messages re-runs on every new
 *  message in the open thread, and a bulk "mark all read" writes in chunks.
 *  Those should cost one refresh between them, not one each. */
const COALESCE_MS = 400

let timer: ReturnType<typeof setTimeout> | null = null

/** Call after anything that changes a sidebar count. */
export function notifyNavBadgesStale(): void {
  if (typeof window === 'undefined') return
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => {
    timer = null
    window.dispatchEvent(new Event(EVENT))
  }, COALESCE_MS)
}

/** Subscribe. Returns the unsubscribe function, for direct use as a useEffect
 *  cleanup. */
export function onNavBadgesStale(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(EVENT, handler)
  return () => window.removeEventListener(EVENT, handler)
}

/** Test seam. */
export function __resetNavBadgesForTests(): void {
  if (timer) clearTimeout(timer)
  timer = null
}
